<template><div><h2 id="in-figma" tabindex="-1" class="d-docsite--header-2">In Figma <a class="header-anchor d-link d-docsite--link d-link" href="#in-figma" aria-hidden="true">#</a></h2>
<div class="d-d-grid d-g-300 d-g-cols1 md:d-g-cols3">
<p class="d-docsite--paragraph">Find a list of available icons in <a href="https://www.figma.com/file/zz40wi0uW9MvaJ5RuhcRZR/DT8-Icon-Library" class="d-docsite--link d-link" target="_blank" rel="noopener noreferrer">DT Core: Icons</a>, or search the icons in the search library within your Figma file.</p>
<dt-stack class="d-gc2" direction="column" gap="100">
<img alt="Figma Search Icon" src="/assets/images/figma-search-icon.gif" style="border-radius: var(--dt-size-radius-400)">
<p class="d-fc-tertiary">Swap instances in Figma by holding ⌘ + ⌥ on Mac, or Ctrl + Alt on Windows.</p>
</dt-stack>
</div>
<h2 id="in-vue" tabindex="-1" class="d-docsite--header-2">In Vue <a class="header-anchor d-link d-docsite--link d-link" href="#in-vue" aria-hidden="true">#</a></h2>
<div class="d-d-grid d-g-300 d-g-cols1 md:d-g-cols3">
<p class="d-docsite--paragraph">For detailed instructions on using the icons, check the <RouteLink to="/components/icon.html" class="d-docsite--link d-link">DtIcon component</RouteLink>.</p>
<div class="d-gc2">
<code-well-header>
  <div class="d-d-grid d-g-100 d-g-cols1 md:d-g-cols3 d-w100p">
    <div class="d-fl-center">
      <dt-icon :name="selectedIcon" :size="selectedSize" />
    </div>
    <dt-select-menu label="Name" :options="iconListOptions" @change="changeIcon" />
    <dt-select-menu label="Size" :options="sizeValues" @change="changeIconSize" />
  </div>
</code-well-header>
</div>
</div>
<h3 id="vue-2" tabindex="-1" class="d-docsite--header-3">Vue 2 <a class="header-anchor d-link d-docsite--link d-link" href="#vue-2" aria-hidden="true">#</a></h3>
<div class="language-javascript" data-highlighter="prismjs" data-ext="js"><pre v-pre><code class="language-javascript"><span class="line"><span class="token keyword">import</span> <span class="token punctuation">{</span> DtIconCreditCard <span class="token punctuation">}</span> <span class="token keyword">from</span> <span class="token string">'@dialpad/dialtone-icons/vue2'</span><span class="token punctuation">;</span></span>
<span class="line"><span class="token operator">&lt;</span>dt<span class="token operator">-</span>icon<span class="token operator">-</span>credit<span class="token operator">-</span>card size<span class="token operator">=</span><span class="token string">"500"</span> aria<span class="token operator">-</span>label<span class="token operator">=</span><span class="token string">"Description"</span> <span class="token operator">/</span><span class="token operator">></span></span>
<span class="line"></span></code></pre>
</div><h3 id="vue-3" tabindex="-1" class="d-docsite--header-3">Vue 3 <a class="header-anchor d-link d-docsite--link d-link" href="#vue-3" aria-hidden="true">#</a></h3>
<div class="language-javascript" data-highlighter="prismjs" data-ext="js"><pre v-pre><code class="language-javascript"><span class="line"><span class="token keyword">import</span> <span class="token punctuation">{</span> DtIconCreditCard <span class="token punctuation">}</span> <span class="token keyword">from</span> <span class="token string">'@dialpad/dialtone-icons/vue'</span><span class="token punctuation">;</span></span>
<span class="line"><span class="token operator">&lt;</span>dt<span class="token operator">-</span>icon<span class="token operator">-</span>credit<span class="token operator">-</span>card size<span class="token operator">=</span><span class="token string">"500"</span> aria<span class="token operator">-</span>label<span class="token operator">=</span><span class="token string">"Description"</span> <span class="token operator">/</span><span class="token operator">></span></span>
<span class="line"></span></code></pre>
</div><h2 id="choosing-the-right-icon" tabindex="-1" class="d-docsite--header-2">Choosing the Right Icon <a class="header-anchor d-link d-docsite--link d-link" href="#choosing-the-right-icon" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Some icons are linked to specific actions, like the Settings gear <dt-icon name="settings" size="200" /> or the Edit pencil <dt-icon name="edit" size="200" />. For actions without a dedicated icon, avoid reusing icons that are already associated with other actions, this helps prevent confusion and ensures clear understanding. Instead, select an existing icon from the Icon Catalog without a specific action meaning or consider <a href="/foundations/icons/crafting-an-icon/" class="d-docsite--link d-link" target="_blank" rel="noopener noreferrer">creating a new one</a> that clearly represents the intended action.</p>
<h2 id="sizing" tabindex="-1" class="d-docsite--header-2">Sizing <a class="header-anchor d-link d-docsite--link d-link" href="#sizing" aria-hidden="true">#</a></h2>
<div>
<div>
<p class="d-docsite--paragraph">The icon size is defined based on the context and text size next to it. These are the only available size options and no overrides should be needed to properly size the icons.</p>
</div>
<dt-stack direction="row" gap="200" class="d-gc1 d-bgc-secondary d-p-200 d-px-400 d-bar-500 d-ai-flex-start d-jc-space-between">
  <dt-stack gap="100" align="center">
    <code>200</code>
    <dt-icon name="food" size="200" />
  </dt-stack>
  <dt-stack gap="100" align="center">
    <code>300</code>
    <dt-icon name="food" size="300" />
  </dt-stack>
  <dt-stack gap="100" align="center">
    <code>400</code>
    <dt-icon name="food" size="400" />
  </dt-stack>
  <dt-stack gap="100" align="center">
    <code>500</code>
    <dt-icon name="food" size="500" />
  </dt-stack>
  <dt-stack gap="100" align="center">
    <code>600</code>
    <dt-icon name="food" size="600" />
  </dt-stack>
  <dt-stack gap="100" align="center">
    <code>700</code>
    <dt-icon name="food" size="700" />
  </dt-stack>
  <dt-stack gap="100" align="center">
    <code>800</code>
    <dt-icon name="food" size="800" />
  </dt-stack>
</dt-stack>
</div>
</div></template>


<script setup>
import { ref } from 'vue';
import sizes from '@data/icons-sizes.json';

const sizeValues = sizes.map(item => ({ value: item.size, label: item.size }));

const iconListOptions = [
  { value: 'user-plus', label: 'User Plus' },
  { value: 'flame', label: 'Flame' },
  { value: 'heart', label: 'Heart' },
  { value: 'credit-card', label: 'Credit Card' }
];

const selectedIcon = ref('settings');
const selectedSize = ref('500');

const changeIcon = (newIcon) => {
  selectedIcon.value = newIcon;
};

const changeIconSize = (newSize) => {
  selectedSize.value = newSize;
};

</script>